import React from 'react'
import styled, { useTheme } from 'styled-components/native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'APP/Hooks/redux'
import Analytics from 'APP/Services/Analytics'

import { TabScreens } from 'APP/Lib/ContentHelpers'
import Button from 'APP/Converse/Button'
import Card from 'APP/Converse/Card'
import Typography from 'APP/Converse/Typography'

import UpdatesAtGlance from 'APP/Images/updates_at_glance.svg'
import I18n, { isTranslationKey } from 'APP/I18n'
import Config from 'APP/Config'

interface UpdatesAtGlanceProps {
  accentFillLine?: string
  fillLine?: string
  width?: string
  height?: string
}

const UpdatesAtGlanceIcon = UpdatesAtGlance as React.FC<UpdatesAtGlanceProps>

const Wrapper = styled.View`
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ActionContainer = styled(Card)<{ hasAskQuestion?: boolean }>`
  ${({ theme }) => theme.shadows.normal};
  background-color: ${({ theme }) => theme.colors.elementsBg};
  border-radius: 20px;
  align-items: center;
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
  ${({ hasAskQuestion, theme }) => hasAskQuestion && `padding: ${theme.metrics.baseMargin * 2}px;`}
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
`

const OverviewContainer = styled(Card)`
  ${({ theme }) => theme.shadows.normal};
  background-color: ${({ theme }) => theme.colors.elementsBg};
  border-radius: 20px;
  align-items: center;
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-top: ${({ theme }) => theme.metrics.baseMargin}px;
  padding-bottom: ${({ theme }) => theme.metrics.baseMargin * 1.5}px;
  margin-horizontal: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-top: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const CardText = styled(Typography)`
  margin-vertical: ${({ theme }) => theme.metrics.baseMargin}px;
`

// Copy variants exist because SL-US doesn't want the word 'care' to be used
const getBrandText = (brandId: string, key: string, subkey: string) => {
  const brandKey = `${key}.${brandId}.${subkey}`
  if (isTranslationKey(brandKey)) return I18n.t(brandKey)
  const defaultKey = `${key}.${subkey}`
  return isTranslationKey(defaultKey) ? I18n.t(defaultKey) : undefined
}

const MainAction = () => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { appointments, orderedEpisodes } = useSelector((state) => state?.history)

  const showUpdatesAtGlance = orderedEpisodes?.length === 0 && appointments?.upcoming?.length === 0

  const navToChat = () => {
    Analytics.trackEvent('button_click', {
      button_value: 'Today.getCareNow',
    })
    navigation.navigate('chatTab', { tabName: TabScreens.chat.getCare })
  }

  return (
    <Wrapper>
      <ActionContainer analyticsName="TodayScreen.getCareNowCard" testID="GET_CARE_NOW_CARD">
        <CardText align="center">
          {getBrandText(Config.BRAND_ID, 'TodayScreen.getCareNowCard', 'body')}
        </CardText>
        <Button
          variant="oomph"
          title={getBrandText(Config.BRAND_ID, 'TodayScreen.getCareNowCard', 'cta')}
          onPress={navToChat}
          testID="GET_CARE_NOW"
        />
      </ActionContainer>
      {showUpdatesAtGlance && (
        <OverviewContainer
          analyticsName="TodayScreen.updatesAtGlanceCard"
          testID="UPDATES_AT_A_GLANCE"
        >
          <CardText align="center" variant="h4">
            {getBrandText(Config.BRAND_ID, 'TodayScreen.updatesAtAGlanceCard', 'title')}
          </CardText>
          <UpdatesAtGlanceIcon
            accentFillLine={theme.colors.elementsBg}
            fillLine={theme.colors.elementsBorder}
            width="180"
            height="101"
          />
          <CardText align="center">
            {getBrandText(Config.BRAND_ID, 'TodayScreen.updatesAtAGlanceCard', 'body')}
          </CardText>
        </OverviewContainer>
      )}
    </Wrapper>
  )
}

export default MainAction
